import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import { Close, Pause, PlayArrow, CheckCircleOutline, Schedule } from '@mui/icons-material';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { formatDuration } from 'components/play/workoutEstimate';
import { phaseAt } from 'components/play/recovery';
import BreathingFigure from 'components/play/recovery/BreathingFigure';
import GuidedStepScreen from 'components/play/recovery/GuidedStepScreen';
import PlayRecoveryIntro from 'components/play/recovery/PlayRecoveryIntro';
import TrimmedVideo from 'components/play/recovery/TrimmedVideo';
import PlayMusicPlayer from 'components/play/music/PlayMusicPlayer';
import {
  stepAt,
  completedExerciseSteps,
  exerciseStepCount
} from 'components/play/recovery/practiceUtils';
import { LOG_PLAY_RECOVERY_SESSION, MY_PLAY_RECOVERY_SESSIONS } from 'api/graphql/playRecovery';

// Menos de esto al cerrar = ni siquiera empezó: no ensuciar el historial.
const MIN_SAVE_SECONDS = 15;

/**
 * OTRIX Play — ejecución de una práctica de recuperación NORMALIZADA (practiceUtils).
 *
 * Tres modos según la práctica: respiración (figura humana al compás), guiada por pasos
 * (yoga/movilidad/custom con auto-avance y descansos) y libre. El reloj va por TIMESTAMPS
 * (no por ticks: un tab en segundo plano no debe desincronizar). Al terminar persiste la
 * sesión (tiempo real, pasos completados, % de logro) para las cards del panel de descanso.
 */
function PlayRecoveryRunner({
  practice = null,
  musicTrack = null,
  musicTracks = null,
  open,
  onClose
}) {
  const { t } = useTranslation(['play']);
  // intro | running | paused | done. El saludo es un estado propio y NO un paso más: si viviera
  // dentro de la secuencia contaminaría totalSeconds, el % de logro y el reloj, y las prácticas
  // de respiración (cero pasos) no tendrían dónde colgarlo.
  const [status, setStatus] = useState('running');
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(0);
  const accumRef = useRef(0);
  const startedAtRef = useRef(null);
  const savedRef = useRef(false);
  const [summary, setSummary] = useState(null);

  const [logSession] = useMutation(LOG_PLAY_RECOVERY_SESSION, {
    refetchQueries: [{ query: MY_PLAY_RECOVERY_SESSIONS, variables: { first: 30 } }]
  });

  const total = practice?.totalSeconds || 0;
  const introUrl = practice?.intro_video_url || null;

  // Reinicio limpio en cada apertura. Con saludo se entra por el intro y el CRONÓMETRO NO
  // ARRANCA: el tiempo de la bienvenida no es tiempo de práctica y falsearía el histórico.
  useEffect(() => {
    if (open) {
      setStatus(introUrl ? 'intro' : 'running');
      setElapsed(0);
      setSummary(null);
      accumRef.current = 0;
      startRef.current = Date.now();
      startedAtRef.current = new Date().toISOString();
      savedRef.current = false;
    }
  }, [open, introUrl, practice?.key, practice?.id]);

  // Arranque real tras el saludo: el reloj se pone a cero AQUÍ, no al abrir el diálogo.
  const startAfterIntro = useCallback(() => {
    accumRef.current = 0;
    startRef.current = Date.now();
    startedAtRef.current = new Date().toISOString();
    setElapsed(0);
    setStatus('running');
  }, []);

  // Tick basado en timestamps.
  useEffect(() => {
    if (!open || status !== 'running') return undefined;
    const timer = setInterval(() => {
      setElapsed(Math.floor((accumRef.current + (Date.now() - startRef.current)) / 1000));
    }, 250);
    return () => clearInterval(timer);
  }, [open, status]);

  const persist = useCallback(
    async (finalElapsed, completed) => {
      if (savedRef.current || !practice) return;
      savedRef.current = true;

      const steps = practice.steps || [];
      const totalExercises = exerciseStepCount(steps);
      const doneExercises = completed ? totalExercises : completedExerciseSteps(steps, finalElapsed);
      const percent = completed
        ? 100
        : Math.min(100, Math.round((finalElapsed / Math.max(1, total)) * 100));

      setSummary({
        seconds: Math.min(finalElapsed, total),
        percent,
        completed,
        doneExercises,
        totalExercises
      });

      try {
        await logSession({
          variables: {
            input: {
              practice_id: practice.id || null,
              practice_key: practice.key || null,
              started_at: startedAtRef.current,
              total_seconds: Math.min(finalElapsed, total),
              completed_steps: doneExercises,
              total_steps: totalExercises,
              completion_percent: percent,
              status: completed ? 'completed' : 'partial'
            }
          }
        });
      } catch {
        // El log es best-effort: la práctica ya se hizo; no se interrumpe el cierre.
      }
    },
    [practice, total, logSession]
  );

  const finish = useCallback(() => {
    // En pausa el acumulado ya está al día; corriendo se suma el tramo vivo.
    const finalElapsed = Math.floor((accumRef.current + (status === 'running' ? Date.now() - startRef.current : 0)) / 1000);
    setStatus('done');
    persist(Math.min(finalElapsed, total), finalElapsed >= total);
  }, [persist, status, total]);

  // Completó el tiempo total: cierre automático a "done".
  useEffect(() => {
    if (open && status === 'running' && total > 0 && elapsed >= total) {
      setStatus('done');
      persist(total, true);
    }
  }, [open, status, elapsed, total, persist]);

  if (!practice) return null;

  const musicList = musicTracks || [];

  const togglePause = () => {
    if (status === 'running') {
      accumRef.current += Date.now() - startRef.current;
      setStatus('paused');
    } else if (status === 'paused') {
      startRef.current = Date.now();
      setStatus('running');
    }
  };

  const handleClose = () => {
    // Cerrar durante el saludo no es haber entrenado: sin esta salida, un intro largo dejaba
    // pasar el mínimo de segundos y quedaba registrada una sesión parcial que nunca ocurrió.
    if (status === 'intro') {
      onClose();
      return;
    }

    // Cerrar sin terminar: si apenas empezó no se guarda nada; si no, queda como parcial.
    if (status !== 'done') {
      const finalElapsed = Math.floor((accumRef.current + (status === 'running' ? Date.now() - startRef.current : 0)) / 1000);
      if (finalElapsed >= MIN_SAVE_SECONDS) {
        persist(Math.min(finalElapsed, total), finalElapsed >= total);
      }
    }
    onClose();
  };

  const remaining = Math.max(0, total - elapsed);
  const running = status === 'running';
  const done = status === 'done';
  const inIntro = status === 'intro';
  const phase = running && practice.pattern ? phaseAt(practice.pattern, elapsed) : null;
  const guided = !practice.pattern && practice.steps.length > 0;
  const current = guided ? stepAt(practice.steps, Math.min(elapsed, Math.max(0, total - 1))) : null;
  const exercisesBefore = current
    ? practice.steps.slice(0, current.index + 1).filter((s) => s.type !== 'rest').length
    : 0;
  const nextStep = current && current.index < practice.steps.length - 1 ? practice.steps[current.index + 1] : null;

  // ¿El video que se está viendo ahora mismo lleva la voz de la entrenadora? Espeja la elección
  // de fuente de GuidedStepScreen (video del paso > video general, y nunca en un descanso): si
  // habla, la música se agacha en vez de taparla.
  const stepHasVoice = (() => {
    // Respiración: no hay pasos, la voz sale del video general de la práctica.
    if (practice.pattern) return Boolean(practice.video_url && practice.video_has_audio);
    if (!current || current.step.type === 'rest') return false;
    if (current.step.video_url) return Boolean(current.step.video_has_audio);
    return Boolean(practice.video_url && practice.video_has_audio);
  })();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Typography variant="h6" fontWeight={800} noWrap>
          {practice.title}
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }} aria-label={t('recovery.close', { ns: 'play' })}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <DialogContent>
        <Stack spacing={2.5} alignItems="center" sx={{ py: 1.5 }}>
          {done && summary && (
            <Stack spacing={1.5} alignItems="center" sx={{ py: 2, textAlign: 'center', width: '100%' }}>
              <CheckCircleOutline
                sx={{ fontSize: 52, color: summary.completed ? 'sessionStatus.completed.fg' : 'sessionStatus.pending.fg' }}
              />
              <Typography variant="h6" fontWeight={800}>
                {summary.completed
                  ? t('recovery.doneTitle', { ns: 'play' })
                  : t('recovery.partialTitle', { ns: 'play', defaultValue: 'Buen avance' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {summary.completed
                  ? t('recovery.doneBody', { ns: 'play' })
                  : t('recovery.partialBody', { ns: 'play', defaultValue: 'Guardamos hasta donde llegaste. Cada minuto suma.' })}
              </Typography>
              {/* Resumen de la sesión: tiempo real + % de logro (+ pasos en guiadas). */}
              <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 1.5, py: 0.6, borderRadius: 999, border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.overlay' }}>
                  <Schedule sx={{ fontSize: 14, color: 'gold.main' }} />
                  <Typography variant="caption" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatDuration(summary.seconds)}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 1.5, py: 0.6, borderRadius: 999, border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.overlay' }}>
                  <Typography variant="caption" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', color: summary.completed ? 'sessionStatus.completed.fg' : 'sessionStatus.pending.fg' }}>
                    {summary.percent}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('recovery.summaryPercent', { ns: 'play', defaultValue: 'logrado' })}
                  </Typography>
                </Stack>
                {summary.totalExercises > 0 && (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 1.5, py: 0.6, borderRadius: 999, border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.overlay' }}>
                    <Typography variant="caption" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {summary.doneExercises}/{summary.totalExercises}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t('recovery.summarySteps', { ns: 'play', defaultValue: 'ejercicios' })}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          )}

          {inIntro && (
            <PlayRecoveryIntro
              videoUrl={introUrl}
              startMs={practice.intro_start_ms}
              endMs={practice.intro_end_ms}
              readyMs={practice.intro_ready_ms}
              hasAudio={practice.intro_has_audio}
              required={practice.intro_required}
              practiceId={practice.id || practice.key}
              onStart={startAfterIntro}
              onCancel={handleClose}
            />
          )}

          {!done && !inIntro && (
            <>
              {/* Respiración. Si la entrenadora subió un video, ES la guía y sustituye a la
                  figura: antes el video de una práctica de respiración se podía cargar desde
                  el panel pero NO se veía en ninguna parte. El rótulo de fase se conserva
                  siempre, porque es lo que marca el ritmo. */}
              {practice.pattern && (
                <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  {practice.video_url ? (
                    <TrimmedVideo
                      src={practice.video_url}
                      startMs={practice.video_start_ms}
                      endMs={practice.video_end_ms}
                      muted={!practice.video_has_audio}
                      loop
                      autoPlay
                      sx={{
                        width: '100%',
                        maxWidth: 320,
                        aspectRatio: '4 / 3',
                        borderRadius: '16px',
                        border: '1px solid',
                        borderColor: 'hairline.main'
                      }}
                    />
                  ) : (
                    <BreathingFigure phase={phase} />
                  )}
                  {phase && (
                    <Typography variant="overline" sx={{ letterSpacing: '0.16em', fontWeight: 800, color: 'gold.main' }}>
                      {t(`recovery.phases.${phase.phase}`, { ns: 'play' })}
                    </Typography>
                  )}
                </Stack>
              )}

              {/* Guiada por pasos (yoga / movilidad / custom de la entrenadora). */}
              {guided && current && (
                <GuidedStepScreen
                  step={current.step}
                  stepElapsed={current.stepElapsed}
                  exerciseNumber={Math.max(1, exercisesBefore)}
                  exerciseTotal={exerciseStepCount(practice.steps)}
                  nextTitle={nextStep ? nextStep.title : null}
                  practiceVideo={{
                    url: practice.video_url,
                    startMs: practice.video_start_ms,
                    endMs: practice.video_end_ms,
                    hasAudio: practice.video_has_audio
                  }}
                />
              )}

              <Stack spacing={0.25} alignItems="center">
                <Typography variant="h2" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                  {formatDuration(remaining)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('recovery.remaining', { ns: 'play' })}
                </Typography>
              </Stack>

              <Button
                variant="outlined"
                startIcon={running ? <Pause /> : <PlayArrow />}
                onClick={togglePause}
                sx={{
                  borderColor: 'hairline.strong',
                  color: 'text.primary',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2.5,
                  '&:active': { transform: 'scale(0.98)' }
                }}>
                {running ? t('workout.pause', { ns: 'play' }) : t('workout.resume', { ns: 'play' })}
              </Button>
            </>
          )}

          {/* Música: siempre visible durante la práctica (play manual: política de autoplay).
              Puede ser una pista suelta o el álbum completo de una categoría. */}
          {!done && !inIntro && (musicList.length > 0 || musicTrack) && (
            <PlayMusicPlayer
              tracks={musicList.length > 0 ? musicList : null}
              track={musicTrack}
              duck={stepHasVoice}
            />
          )}

          {/* Durante el saludo el CTA lo pone PlayRecoveryIntro (es su puerta): un segundo
              botón "Terminar" ahí abajo invitaría a cerrar antes de empezar. */}
          {!inIntro && (
            <Button fullWidth onClick={done ? onClose : finish} sx={{ ...premiumButtonSx }}>
              {done ? t('recovery.close', { ns: 'play' }) : t('workout.finish', { ns: 'play' })}
            </Button>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

PlayRecoveryRunner.propTypes = {
  /** Práctica NORMALIZADA (practiceUtils.normalizePractice / fallbackPractices). */
  practice: PropTypes.object,
  /** Pista suelta (API heredada: la usa el check-in de bienestar). */
  musicTrack: PropTypes.object,
  /** Lista completa a encadenar durante la práctica (álbum por categoría). */
  musicTracks: PropTypes.array,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default PlayRecoveryRunner;
